import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { createSelector } from 'reselect';

import { Col, Container, DefaultTheme, Div, Icon, Row } from '@pinpoint/design-kit';
import { charts, components } from '@pinpoint/onpoint-charts';

import { ApiWrapper, GraphicCard, Button } from 'src/components';
import { appActions, clustersActions } from 'src/store/actions';
import { Rbac, GraphicPage } from 'src/containers';

import CONSTANTS from 'src/store/constants';
import { ROUTES } from 'src/config';

const RadarChart = components.withFixedTitle(charts.Radar);

const segmentsFilter = ({ segments }) => segments;
const clusteringFilter = ({ clustering }) => clustering;

const segmentSelector = createSelector([segmentsFilter], (segments) =>
  segments.length === 1 ? segments[0] : { clusters: [], dataset: { name: '' } }
);

const v3ClustersSelector = createSelector([segmentSelector], (segment) =>
  segment.clusters.filter(({ filter }) => filter.match(CONSTANTS.SEGMENT.FILTER.CLUSTERING_REGEX))
);

const selectedV3ClustersSelector = createSelector([v3ClustersSelector, clusteringFilter], (v3Clusters, clustering) => {
  const regExp = new RegExp(`CLUSTERING_V\\d/${clustering}`, 'g');
  return v3Clusters.filter(({ filter }) => filter.match(regExp));
});

const selectedV3ClustersRadarSelector = createSelector([selectedV3ClustersSelector], (selectedV3Clusters) => {
  const objects = [];
  selectedV3Clusters.forEach(({ pxScores, ...rest }) => {
    const pxScoresCopy = pxScores.slice();
    pxScoresCopy.sort(({ trait: { code: codeA } }, { trait: { code: codeB } }) => (codeA > codeB ? 1 : -1));
    const newPxScores = pxScoresCopy.filter(({ trait: { subType } }) => subType !== 'factor' && subType !== 'redacted');
    const segments = newPxScores.map(({ trait, score }) => ({
      trait,
      score,
      order: Math.abs(50 - score),
    }));
    segments.sort(({ order: orderA }, { order: orderB }) => orderB - orderA);
    const labels = segments.map(({ trait }) => trait);
    const values = segments.map(({ score }) => score);
    objects.push({
      labels,
      values,
      ...rest,
    });
  });
  return objects;
});

const clusteringsSelector = createSelector([v3ClustersSelector], (v3Clusters) => {
  const clusterings = v3Clusters
    .map(({ filter }) => {
      const regexMatch = filter.match(CONSTANTS.SEGMENT.FILTER.CLUSTERING_REGEX);
      if (regexMatch.groups && regexMatch.groups.clustering) {
        return parseInt(regexMatch.groups.clustering, 10);
      }
      return null;
    })
    .filter((value) => !!value);
  const clusteringsArray = [...new Set(clusterings)];
  clusteringsArray.sort();
  return clusteringsArray;
});

function Clusters({
  history,
  match: {
    params: { segmentId: segmentIdParam },
  },
  app: {
    tenant,
    profile: { role, siteRole },
  },
  clusters: { clustering, fetchSegmentsStatus, editClusterStatus, saveClusteringStatus },
  v3Clusters,
  segment,
  selectedV3ClustersRadar,
  clusterings,
  fetchSegments,
  saveClustering,
  setClustering,
  editCluster,
  enqueueNotification,
}) {
  const [isAdmin, setIsAdmin] = useState(role === CONSTANTS.ROLE.ADMIN || siteRole === CONSTANTS.ROLE.SUPER_ADMIN);

  const getData = useCallback(
    (segmentId) => {
      const query = {
        filters: [
          {
            and: [{ name: 'id', op: 'eq', val: segmentId }],
          },
        ],
      };
      fetchSegments(query);
    },
    [fetchSegments]
  );

  useEffect(() => {
    setIsAdmin(role === CONSTANTS.ROLE.ADMIN || siteRole === CONSTANTS.ROLE.SUPER_ADMIN);
  }, [role, siteRole]);

  useEffect(() => {
    if (saveClusteringStatus === CONSTANTS.API.SUCCEEDED) {
      enqueueNotification('Clusters have been successfully saved', { autoClose: true });
    } else if (saveClusteringStatus === CONSTANTS.API.FAILED) {
      enqueueNotification('There was an error saving the clusters. If the problem continues, please contact support');
    }
  }, [saveClusteringStatus, enqueueNotification]);

  useEffect(() => {
    if (editClusterStatus === CONSTANTS.API.FAILED) {
      enqueueNotification('There was an error updating cluster name. If the problem continues, please contact support');
    }
  }, [editClusterStatus, enqueueNotification]);

  useEffect(() => {
    if (!clustering && v3Clusters.length > 0) {
      // Check if we have selected clusters
      let selectedCluster = v3Clusters.find(({ filter }) =>
        filter.match(CONSTANTS.SEGMENT.FILTER.SELECTED_CLUSTERING_REGEX)
      );
      if (!selectedCluster) {
        [selectedCluster] = v3Clusters;
      }
      const regexMatch = selectedCluster.filter.match(CONSTANTS.SEGMENT.FILTER.CLUSTERING_REGEX);
      setClustering(parseInt(regexMatch.groups.clustering, 10));
    }
  }, [clustering, v3Clusters, setClustering]);

  useEffect(() => {
    if (tenant) getData(segmentIdParam);
  }, [tenant, segmentIdParam, getData]);

  return (
    <ApiWrapper
      hasData={selectedV3ClustersRadar && selectedV3ClustersRadar.length > 0}
      getData={() => {
        getData(segmentIdParam);
      }}
      apiStatus={fetchSegmentsStatus}
    >
      <Container w="100%" minW="100%">
        <Row p={{ t: '1rem', b: '0.5rem' }}>
          <Col size={12} p="1em">
            <Div m={{ b: '0.5rem' }} d="flex" justify="space-between">
              <Div d="flex">
                {clusterings.map((clusteringValue) => (
                  <Button
                    buttonStyle="tab"
                    selected={clusteringValue === clustering}
                    data-tip={`View ${clusteringValue} clusters`}
                    onClick={() => {
                      setClustering(clusteringValue);
                    }}
                  >
                    {clusteringValue}
                  </Button>
                ))}
                <Div p={{ x: '0.4rem' }}>
                  <Rbac
                    roles={[CONSTANTS.ROLE.ADMIN, CONSTANTS.ROLE.SUPER_ADMIN]}
                    fallback={
                      <Button buttonStyle="primary" disabled>
                        <Icon name="Plus" color="white" size="18px" m={{ r: '.5rem' }} />
                        SAVE
                      </Button>
                    }
                  >
                    <Button
                      buttonStyle="primary"
                      data-tip="Save clusters below in Datasets table"
                      onClick={() => {
                        saveClustering(clustering);
                      }}
                    >
                      <Icon name="Plus" color="white" size="18px" m={{ r: '.5rem' }} />
                      SAVE
                    </Button>
                  </Rbac>
                </Div>
              </Div>
              <Div d="flex" justify="flex-end" align="center" m={{ r: '-0.5rem' }}>
                <Button
                  buttonStyle="primary"
                  data-tip="Return to parent dataset"
                  onClick={() => {
                    history.push(ROUTES.VIEW_SEGMENTS.replace(':entity', 'segment').replace(':entityId', segment.id));
                  }}
                >
                  <Icon name="Visualize" color="white" size="1rem" m={{ r: '0.5rem' }} />
                  VISUALIZE
                </Button>
              </Div>
            </Div>
          </Col>
        </Row>
        <Row
          justify="center"
          align="center"
          h="calc(100vh - 9.75rem)"
          bg="white"
          p={{ x: '1rem', y: '0.75rem' }}
          m={{ x: '0' }}
          rounded="md"
        >
          {selectedV3ClustersRadar.map((cluster, index) => (
            <Col
              // eslint-disable-next-line react/no-array-index-key
              key={`col-${cluster.filter}-${index}`}
              h={clustering <= 2 ? 'calc(100% - 1.5em)' : 'calc(50% - 1.5em)'}
              size={clustering <= 4 ? 6 : 4}
              justify="space-between"
              align="center"
              p={{ x: '0', y: '0' }}
              m={{ b: '1.25rem' }}
              gutterWidth="1.25rem"
            >
              <GraphicCard
                title={cluster.name}
                editTitle={isAdmin ? CONSTANTS.CONTROL_STATES.ENABLED : CONSTANTS.CONTROL_STATES.DISABLED}
                onEdit={(text) => {
                  editCluster(cluster.id, { name: text });
                }}
                cardProps={{
                  bg: 'secondary',
                  rounded: 'md',
                }}
              >
                <RadarChart
                  title=""
                  labels={cluster.labels.slice(0, 7)}
                  series={[
                    {
                      name: cluster.name,
                      values: cluster.values.slice(0, 7),
                    },
                  ]}
                  options={{
                    backgroundColor: DefaultTheme.colors.secondary,
                    radar: {
                      radius: selectedV3ClustersRadar.length === 2 ? '60%' : '80%',
                      center: ['50%', '55%'],
                      name: {
                        fontSize: selectedV3ClustersRadar.length >= 5 ? 13 : 15,
                      },
                    },
                    label: {
                      fontSize: selectedV3ClustersRadar.length >= 5 ? 13 : 15,
                    },
                  }}
                  showRangeControl
                  /**
                   * `light` is the default theme which means a theme is not explicitly
                   * set in the cluster in which case we give it a random theme. else
                   * we used the theme set on the cluster
                   */
                  theme={cluster.theme === 'light' ? (index ? `pinpoint${index}` : 'pinpoint') : cluster.theme}
                  relative
                />
              </GraphicCard>
            </Col>
          ))}
        </Row>
      </Container>
    </ApiWrapper>
  );
}

const mapStateToProps = ({ app, clusters }) => ({
  app,
  clusters,
  segment: segmentSelector(clusters),
  v3Clusters: v3ClustersSelector(clusters),
  selectedV3ClustersRadar: selectedV3ClustersRadarSelector(clusters),
  clusterings: clusteringsSelector(clusters),
});

const mapDispatchToProps = (dispatch) => ({
  fetchSegments: (...params) => dispatch(clustersActions.fetchSegments(...params)),
  saveClustering: (...params) => dispatch(clustersActions.saveClustering(...params)),
  setClustering: (...params) => dispatch(clustersActions.setClustering(...params)),
  editCluster: (...params) => dispatch(clustersActions.editCluster(...params)),
  enqueueNotification: (...params) => dispatch(appActions.enqueueNotification(...params)),
});

Clusters.propTypes = {
  app: PropTypes.shape({
    tenant: PropTypes.string.isRequired,
    profile: PropTypes.shape({
      role: PropTypes.string.isRequired,
      siteRole: PropTypes.string.isRequired,
    }),
  }).isRequired,
  clusters: PropTypes.shape({
    segments: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number.isRequired,
        dataset: PropTypes.shape({
          name: PropTypes.string,
        }),
      })
    ),
    fetchSegmentsStatus: PropTypes.oneOf([
      CONSTANTS.API.STARTED,
      CONSTANTS.API.SUCCEEDED,
      CONSTANTS.API.FAILED,
      undefined,
    ]),
    clustering: PropTypes.number,
    saveClusteringStatus: PropTypes.oneOf([
      CONSTANTS.API.STARTED,
      CONSTANTS.API.SUCCEEDED,
      CONSTANTS.API.FAILED,
      undefined,
    ]),
    editClusterStatus: PropTypes.oneOf([
      CONSTANTS.API.STARTED,
      CONSTANTS.API.SUCCEEDED,
      CONSTANTS.API.FAILED,
      undefined,
    ]),
  }).isRequired,
  segment: PropTypes.shape({ id: PropTypes.number }).isRequired,
  v3Clusters: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  selectedV3ClustersRadar: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  clusterings: PropTypes.arrayOf(PropTypes.number).isRequired,
  fetchSegments: PropTypes.func.isRequired,
  saveClustering: PropTypes.func.isRequired,
  setClustering: PropTypes.func.isRequired,
  editCluster: PropTypes.func.isRequired,
  enqueueNotification: PropTypes.func.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(GraphicPage(Clusters));
